/**
 * Servicio para el cálculo de precios dinámicos según la duración de la reserva.
 * Incluye cálculo de base imponible e IVA por línea para que el total coincida con Quipu.
 */

import { formatPricePerDay } from '../utils/priceFormatter';
import { calculateDynamicPriceWithSeasons, getActiveSeasons } from './seasonalPricingService';

const VAT_PERCENT = 21;

/**
 * Dado un importe total con IVA, devuelve { base, vat } a 2 decimales tal que base + vat === total.
 */
function splitTotalIntoBaseAndVat(total, vatPercent = VAT_PERCENT) {
  const t = Math.round(total * 100) / 100;
  const baseApprox = t / (1 + vatPercent / 100);
  for (const delta of [0, 0.01, -0.01, 0.02, -0.02]) {
    const base = Math.round((baseApprox + delta) * 100) / 100;
    const vat = Math.round(base * (vatPercent / 100) * 100) / 100;
    if (Math.round((base + vat) * 100) / 100 === t) return { base, vat, total: t };
  }
  const base = Math.round(baseApprox * 100) / 100;
  const vat = Math.round((t - base) * 100) / 100;
  return { base, vat, total: t };
}

/**
 * Convierte líneas (concept, amount) en líneas con base e IVA para que la suma coincida con grandTotal.
 * La última línea absorbe el redondeo.
 * @param {{ concept: string, amount: number }[]} lineItems
 * @param {number} grandTotal
 * @returns {{ concept: string, base: number, vat: number, total: number }[]}
 */
export function splitLineAmountsIntoBaseAndVat(lineItems, grandTotal, vatPercent = VAT_PERCENT) {
  const totalR = Math.round(grandTotal * 100) / 100;
  if (!lineItems || lineItems.length === 0) {
    const { base, vat, total } = splitTotalIntoBaseAndVat(totalR, vatPercent);
    return [{ concept: 'Total', base, vat, total }];
  }
  if (lineItems.length === 1) {
    const { base, vat, total } = splitTotalIntoBaseAndVat(totalR, vatPercent);
    return [{ concept: lineItems[0].concept, base, vat, total }];
  }
  const result = [];
  let runningTotal = 0;
  for (let i = 0; i < lineItems.length - 1; i++) {
    const amount = Math.round(lineItems[i].amount * 100) / 100;
    const { base, vat, total } = splitTotalIntoBaseAndVat(amount, vatPercent);
    result.push({ concept: lineItems[i].concept, base, vat, total });
    runningTotal += total;
  }
  const lastRequired = Math.round((totalR - runningTotal) * 100) / 100;
  const { base, vat, total } = splitTotalIntoBaseAndVat(lastRequired, vatPercent);
  result.push({ concept: lineItems[lineItems.length - 1].concept, base, vat, total });
  return result;
}

/**
 * Calcula el precio total de un vehículo según la duración de la reserva (versión local)
 * @param {Object} vehicle - Objeto del vehículo con basePrice, threeToFive, additionalDay
 * @param {number} durationInDays - Duración de la reserva en días
 * @returns {number} Precio total calculado
 */
export const calculateDynamicPrice = (vehicle, durationInDays) => {
  if (!vehicle || !durationInDays || durationInDays < 1) {
    return 0;
  }

  // Validar que el vehículo tenga los campos de precio necesarios
  if (typeof vehicle.basePrice !== 'number' || 
      typeof vehicle.threeToFive !== 'number' || 
      typeof vehicle.additionalDay !== 'number') {
    console.warn('Vehículo sin campos de precio válidos:', vehicle);
    return 0;
  }

  // Días 1-2: Precio base
  if (durationInDays <= 2) {
    return vehicle.basePrice * durationInDays;
  }

  // Días 3-5: Aplicar descuento porcentual
  if (durationInDays >= 3 && durationInDays <= 5) {
    const baseTotal = vehicle.basePrice * durationInDays;
    const discountAmount = baseTotal * (vehicle.threeToFive / 100);
    return baseTotal - discountAmount;
  }

  // Días 6+: Precio mixto
  // Días 1-5: precio base con descuento
  // Días 6+: precio adicional
  const first5DaysBase = vehicle.basePrice * 5;
  const first5DaysDiscount = first5DaysBase * (vehicle.threeToFive / 100);
  const first5DaysTotal = first5DaysBase - first5DaysDiscount;
  const additionalDays = durationInDays - 5;
  const additionalDaysTotal = vehicle.additionalDay * additionalDays;
  
  return first5DaysTotal + additionalDaysTotal;
};

/**
 * Calcula el precio total de un vehículo usando precios estacionales del backend
 * @param {Object} vehicle - Objeto del vehículo
 * @param {number} durationInDays - Duración de la reserva en días
 * @param {Date|string} startDate - Fecha de inicio de la reserva
 * @param {Date|string} endDate - Fecha de fin de la reserva (opcional)
 * @returns {Promise<Object>} Precio calculado con información de temporada
 */
export const calculateDynamicPriceWithSeasonsAsync = async (vehicle, durationInDays, startDate, endDate = null) => {
  try {
    return await calculateDynamicPriceWithSeasons(vehicle, durationInDays, startDate, endDate);
  } catch (error) {
    console.error('Error calculando precio con temporadas:', error);
    
    // Fallback a cálculo local
    const localPrice = calculateDynamicPrice(vehicle, durationInDays);
    return {
      totalPrice: localPrice,
      basePrice: vehicle.basePrice,
      threeToFive: vehicle.threeToFive,
      additionalDay: vehicle.additionalDay,
      durationInDays,
      discountApplied: 0,
      additionalDaysPrice: 0,
      season: null,
      pricingType: 'default',
      pricePerDay: Math.round((localPrice / durationInDays) * 100) / 100,
      error: 'Error calculando precios estacionales'
    };
  }
};

/**
 * Calcula el precio promedio por día de un vehículo según la duración
 * @param {Object} vehicle - Objeto del vehículo con basePrice, threeToFive, additionalDay
 * @param {number} durationInDays - Duración de la reserva en días
 * @returns {number} Precio promedio por día
 */
export const getPricePerDay = (vehicle, durationInDays) => {
  if (!vehicle || !durationInDays || durationInDays < 1) {
    return 0;
  }

  // Validar campos de precio
  if (typeof vehicle.basePrice !== 'number' || 
      typeof vehicle.threeToFive !== 'number' || 
      typeof vehicle.additionalDay !== 'number') {
    console.warn('Vehículo sin campos de precio válidos:', vehicle);
    return 0;
  }

  // Días 1-2: Precio base
  if (durationInDays <= 2) {
    return vehicle.basePrice;
  }

  // Días 3-5: Precio con descuento aplicado
  if (durationInDays >= 3 && durationInDays <= 5) {
    const baseTotal = vehicle.basePrice * durationInDays;
    const discountAmount = baseTotal * (vehicle.threeToFive / 100);
    const totalPrice = baseTotal - discountAmount;
    return totalPrice / durationInDays;
  }

  // Días 6+: Precio promedio
  const first5DaysBase = vehicle.basePrice * 5;
  const first5DaysDiscount = first5DaysBase * (vehicle.threeToFive / 100);
  const first5DaysTotal = first5DaysBase - first5DaysDiscount;
  const additionalDays = durationInDays - 5;
  const additionalDaysTotal = vehicle.additionalDay * additionalDays;
  const totalPrice = first5DaysTotal + additionalDaysTotal;
  
  return totalPrice / durationInDays;
};

/**
 * Genera una descripción del precio aplicado según la duración
 * @param {Object} vehicle - Objeto del vehículo con basePrice, threeToFive, additionalDay
 * @param {number} durationInDays - Duración de la reserva en días
 * @returns {string} Descripción del precio aplicado
 */
export const getPriceDescription = (vehicle, durationInDays, dayText = 'día') => {
  if (!vehicle || !durationInDays || durationInDays < 1) {
    return 'Precio no disponible';
  }

  // Priorizar precio medio real por día si viene del backend (availability/pricing)
  if (typeof vehicle.pricePerDay === 'number' && !Number.isNaN(vehicle.pricePerDay)) {
    return formatPricePerDay(vehicle.pricePerDay, dayText);
  }

  // Determinar unidad base de precio como fallback
  const unitBase = (typeof vehicle.basePrice === 'number' && !Number.isNaN(vehicle.basePrice))
    ? vehicle.basePrice
    : ((typeof vehicle.basePriceModel === 'number' && !Number.isNaN(vehicle.basePriceModel))
        ? vehicle.basePriceModel
        : undefined);

  // Si no hay ninguna unidad base, no podemos calcular
  if (typeof unitBase === 'undefined') {
    return 'Precio no disponible';
  }

  if (durationInDays === 1 || durationInDays === 2) {
    return formatPricePerDay(unitBase, dayText);
  } else if (durationInDays >= 3 && durationInDays <= 5) {
    if (typeof vehicle.threeToFive === 'number' && !Number.isNaN(vehicle.threeToFive)) {
      const baseTotal = unitBase * durationInDays;
      const discountAmount = baseTotal * (vehicle.threeToFive / 100);
      const totalPrice = baseTotal - discountAmount;
      const pricePerDay = totalPrice / durationInDays;
      return `${formatPricePerDay(pricePerDay, dayText)} (descuento ${vehicle.threeToFive}%)`;
    }
    return formatPricePerDay(unitBase, dayText);
  } else if (durationInDays > 5) {
    const averagePrice = getPricePerDay({ ...vehicle, basePrice: unitBase }, durationInDays);
    return `${formatPricePerDay(averagePrice, dayText)} (promedio)`;
  }

  return formatPricePerDay(unitBase, dayText);
};

/**
 * Calcula la duración en días entre dos fechas
 * @param {Date|string} startDate - Fecha de inicio
 * @param {Date|string} endDate - Fecha de fin
 * @returns {number} Duración en días (mínimo 1)
 */
export const calculateDurationInDays = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return 1;
  }

  try {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      console.warn('Fechas inválidas:', { startDate, endDate });
      return 1;
    }

    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(1, diffDays);
  } catch (error) {
    console.error('Error calculando duración:', error);
    return 1;
  }
};

/**
 * Calcula el precio total de una lista de vehículos
 * @param {Array} vehicles - Array de vehículos
 * @param {number} durationInDays - Duración de la reserva en días
 * @returns {number} Precio total de todos los vehículos
 */
export const calculateVehiclesTotal = (vehicles, durationInDays) => {
  if (!Array.isArray(vehicles) || !durationInDays || durationInDays < 1) {
    return 0;
  }

  return vehicles.reduce((total, vehicle) => {
    return total + calculateDynamicPrice(vehicle, durationInDays);
  }, 0);
};

/**
 * Calcula el precio total de accesorios
 * @param {Object} selectedAccessories - Objeto con accesorios seleccionados por vehículo
 * @param {Array} accessoriesList - Lista completa de accesorios disponibles
 * @param {number} durationInDays - Duración de la reserva en días
 * @returns {Object} Objeto con items de accesorios y total
 */
export const calculateAccessoriesTotal = (selectedAccessories, accessoriesList, durationInDays) => {
  if (!selectedAccessories || !accessoriesList || !durationInDays || durationInDays < 1) {
    return { items: [], total: 0, insuranceTotal: 0 };
  }

  const accessoryItems = [];
  let accessoriesTotal = 0;
  let insuranceTotal = 0;

  Object.entries(selectedAccessories).forEach(([vehicleId, accessoryIds]) => {
    if (!Array.isArray(accessoryIds)) return;

    accessoryIds.forEach(accessoryId => {
      const accessory = accessoriesList.find(a => a.id === accessoryId);
      if (accessory) {
        const isDaily = accessory.accessoryPriceType === 'DAILY';
        const price = isDaily ? accessory.price * durationInDays : accessory.price;

        if (accessory.category === 'INSURANCE') {
          insuranceTotal += price;
        }
        accessoriesTotal += price;

        accessoryItems.push({
          id: accessory.id,
          name: accessory.name,
          description: accessory.description,
          priceType: accessory.accessoryPriceType,
          pricePerDay: isDaily ? accessory.price : null,
          fixedPrice: isDaily ? null : accessory.price,
          totalPrice: price,
          vehicleId: parseInt(vehicleId),
          category: accessory.category
        });
      }
    });
  });

  return {
    items: accessoryItems,
    total: Math.round(accessoriesTotal * 100) / 100,
    insuranceTotal: Math.round(insuranceTotal * 100) / 100
  };
};

/**
 * Calcula el coste de entrega
 * @param {string} deliveryType - Tipo de entrega ('pickup', 'delivery')
 * @param {number} customCost - Coste personalizado de entrega (opcional)
 * @returns {number} Coste de entrega
 */
export const calculateDeliveryCost = (deliveryType, customCost = null) => {
  if (deliveryType === 'delivery') {
    // Si se proporciona un coste personalizado, usarlo
    if (customCost !== null && customCost !== undefined) {
      return parseFloat(customCost);
    }
    return 30.0; // Coste por defecto de 30€ por entrega a domicilio
  }
  return 0.0; // Sin coste para recogida en tienda
};

/**
 * Recalcula accesorios y entrega manteniendo fijo el precio de las bicis ya cotizado
 * (por temporadas/backend), para no reintroducir el precio local (que usa una fórmula
 * de duración distinta a la del backend y puede desincronizarse del importe real a cobrar).
 * @param {Object} vehiclesResult - { items, total, base, vat } ya calculado (p.ej. bookingData.pricing.vehicles)
 * @param {Object} selectedAccessories - Accesorios seleccionados por vehículo
 * @param {Object} dates - Objeto con fechas de inicio y fin
 * @param {Array} accessoriesList - Lista completa de accesorios disponibles
 * @param {string|Object} deliveryTypeOrOption - Tipo de entrega ('pickup', 'delivery') o objeto con {type, cost}
 * @returns {Object} Objeto con pricing completo, con el mismo shape que calculateCompletePricing
 */
export const recalculateAccessoriesAndDeliveryPricing = (vehiclesResult, selectedAccessories, dates, accessoriesList, deliveryTypeOrOption = 'pickup') => {
  const durationInDays = calculateDurationInDays(dates?.start, dates?.end);
  const vehiclesTotal = Math.round((Number(vehiclesResult?.total) || 0) * 100) / 100;

  const accessoriesResult = calculateAccessoriesTotal(selectedAccessories, accessoriesList, durationInDays);

  let deliveryType = deliveryTypeOrOption;
  let customDeliveryCost = null;
  if (typeof deliveryTypeOrOption === 'object' && deliveryTypeOrOption !== null) {
    deliveryType = deliveryTypeOrOption.type || 'pickup';
    customDeliveryCost = deliveryTypeOrOption.cost;
  }
  const deliveryCost = calculateDeliveryCost(deliveryType, customDeliveryCost);

  const accessoriesOnlyTotal = Math.max(0, (accessoriesResult.total ?? 0) - (accessoriesResult.insuranceTotal ?? 0));
  const insuranceTotal = accessoriesResult.insuranceTotal ?? 0;

  const lineAmounts = [
    ...(vehiclesTotal > 0 ? [{ concept: 'vehicles', amount: vehiclesTotal }] : []),
    ...(insuranceTotal > 0 ? [{ concept: 'insurance', amount: insuranceTotal }] : []),
    ...(accessoriesOnlyTotal > 0 ? [{ concept: 'accessories', amount: accessoriesOnlyTotal }] : []),
    ...(deliveryCost > 0 ? [{ concept: 'delivery', amount: deliveryCost }] : [])
  ];
  const rawGrandTotal = Math.round((vehiclesTotal + accessoriesResult.total + deliveryCost) * 100) / 100;
  const linesWithVat = splitLineAmountsIntoBaseAndVat(lineAmounts, rawGrandTotal, VAT_PERCENT);
  const grandTotal = linesWithVat.reduce((s, l) => s + l.total, 0);

  const vehiclesLine = linesWithVat.find(l => l.concept === 'vehicles');
  const insuranceLine = linesWithVat.find(l => l.concept === 'insurance');
  const accessoriesLine = linesWithVat.find(l => l.concept === 'accessories');
  const deliveryLine = linesWithVat.find(l => l.concept === 'delivery');

  return {
    durationInDays,
    vehicles: {
      items: vehiclesResult?.items ?? [],
      total: vehiclesTotal,
      base: vehiclesLine?.base ?? Math.round((vehiclesTotal / (1 + VAT_PERCENT / 100)) * 100) / 100,
      vat: vehiclesLine?.vat ?? Math.round((vehiclesTotal - (vehiclesLine?.base ?? 0)) * 100) / 100
    },
    accessories: {
      items: accessoriesResult.items,
      total: accessoriesResult.total,
      insuranceTotal,
      insuranceBase: insuranceLine?.base ?? 0,
      insuranceVat: insuranceLine?.vat ?? 0,
      accessoriesOnlyTotal,
      accessoriesOnlyBase: accessoriesLine?.base ?? 0,
      accessoriesOnlyVat: accessoriesLine?.vat ?? 0
    },
    delivery: {
      type: deliveryType,
      cost: deliveryCost,
      base: deliveryLine?.base ?? 0,
      vat: deliveryLine?.vat ?? 0
    },
    lines: linesWithVat,
    grandTotal
  };
};

/**
 * Calcula el pricing completo para una reserva (versión local)
 * @param {Array} vehicles - Array de vehículos seleccionados
 * @param {Object} selectedAccessories - Accesorios seleccionados por vehículo
 * @param {Object} dates - Objeto con fechas de inicio y fin
 * @param {Array} accessoriesList - Lista completa de accesorios disponibles
 * @param {string|Object} deliveryTypeOrOption - Tipo de entrega ('pickup', 'delivery') o objeto con {type, cost}
 * @returns {Object} Objeto con pricing completo
 */
export const calculateCompletePricing = (vehicles, selectedAccessories, dates, accessoriesList, deliveryTypeOrOption = 'pickup') => {
  if (!dates?.start || !dates?.end) {
    return {
      durationInDays: 0,
      vehicles: { items: [], total: 0 },
      accessories: { items: [], total: 0 },
      delivery: { cost: 0 },
      grandTotal: 0
    };
  }

  const durationInDays = calculateDurationInDays(dates.start, dates.end);

  // Calcular vehículos con precio dinámico
  const vehicleItems = vehicles.map(vehicle => {
    const totalPrice = calculateDynamicPrice(vehicle, durationInDays);
    const pricePerDay = getPricePerDay(vehicle, durationInDays);
    
    return {
      id: vehicle.id,
      model: vehicle.model,
      size: vehicle.size,
      serialNumber: vehicle.serialNumber,
      basePrice: vehicle.basePrice,
      threeToFive: vehicle.threeToFive,
      additionalDay: vehicle.additionalDay,
      pricePerDay: pricePerDay,
      totalPrice: totalPrice,
      durationInDays: durationInDays
    };
  });

  const vehiclesTotal = Math.round(vehicleItems.reduce((total, item) => total + item.totalPrice, 0) * 100) / 100;

  // Calcular accesorios
  const accessoriesResult = calculateAccessoriesTotal(selectedAccessories, accessoriesList, durationInDays);

  // Obtener tipo de entrega y coste personalizado
  let deliveryType = deliveryTypeOrOption;
  let customDeliveryCost = null;
  
  if (typeof deliveryTypeOrOption === 'object' && deliveryTypeOrOption !== null) {
    deliveryType = deliveryTypeOrOption.type || 'pickup';
    customDeliveryCost = deliveryTypeOrOption.cost;
  }

  // Calcular coste de entrega
  const deliveryCost = calculateDeliveryCost(deliveryType, customDeliveryCost);

  const accessoriesOnlyTotal = Math.max(0, (accessoriesResult.total ?? 0) - (accessoriesResult.insuranceTotal ?? 0));
  const insuranceTotal = accessoriesResult.insuranceTotal ?? 0;

  const lineAmounts = [
    ...(vehiclesTotal > 0 ? [{ concept: 'vehicles', amount: vehiclesTotal }] : []),
    ...(insuranceTotal > 0 ? [{ concept: 'insurance', amount: insuranceTotal }] : []),
    ...(accessoriesOnlyTotal > 0 ? [{ concept: 'accessories', amount: accessoriesOnlyTotal }] : []),
    ...(deliveryCost > 0 ? [{ concept: 'delivery', amount: deliveryCost }] : [])
  ];
  const rawGrandTotal = Math.round((vehiclesTotal + accessoriesResult.total + deliveryCost) * 100) / 100;
  const linesWithVat = splitLineAmountsIntoBaseAndVat(lineAmounts, rawGrandTotal, VAT_PERCENT);
  const grandTotal = linesWithVat.reduce((s, l) => s + l.total, 0);

  const vehiclesLine = linesWithVat.find(l => l.concept === 'vehicles');
  const insuranceLine = linesWithVat.find(l => l.concept === 'insurance');
  const accessoriesLine = linesWithVat.find(l => l.concept === 'accessories');
  const deliveryLine = linesWithVat.find(l => l.concept === 'delivery');

  return {
    durationInDays,
    vehicles: {
      items: vehicleItems,
      total: vehiclesTotal,
      base: vehiclesLine?.base ?? Math.round((vehiclesTotal / (1 + VAT_PERCENT / 100)) * 100) / 100,
      vat: vehiclesLine?.vat ?? Math.round((vehiclesTotal - (vehiclesLine?.base ?? 0)) * 100) / 100
    },
    accessories: {
      items: accessoriesResult.items,
      total: accessoriesResult.total,
      insuranceTotal,
      insuranceBase: insuranceLine?.base ?? 0,
      insuranceVat: insuranceLine?.vat ?? 0,
      accessoriesOnlyTotal,
      accessoriesOnlyBase: accessoriesLine?.base ?? 0,
      accessoriesOnlyVat: accessoriesLine?.vat ?? 0
    },
    delivery: {
      type: deliveryType,
      cost: deliveryCost,
      base: deliveryLine?.base ?? 0,
      vat: deliveryLine?.vat ?? 0
    },
    lines: linesWithVat,
    grandTotal
  };
};

/**
 * Calcula el pricing completo para una reserva usando precios estacionales
 * @param {Array} vehicles - Array de vehículos seleccionados
 * @param {Object} selectedAccessories - Accesorios seleccionados por vehículo
 * @param {Object} dates - Objeto con fechas de inicio y fin
 * @param {Array} accessoriesList - Lista completa de accesorios disponibles
 * @param {string} deliveryType - Tipo de entrega ('pickup', 'delivery')
 * @returns {Promise<Object>} Objeto con pricing completo incluyendo información de temporadas
 */
export const calculateCompletePricingWithSeasons = async (vehicles, selectedAccessories, dates, accessoriesList, deliveryTypeOrOption = 'pickup') => {
  if (!dates?.start || !dates?.end) {
    return {
      durationInDays: 0,
      vehicles: { items: [], total: 0 },
      accessories: { items: [], total: 0, insuranceTotal: 0 },
      delivery: { cost: 0 },
      grandTotal: 0,
      seasons: [],
      pricingType: 'default'
    };
  }

  const durationInDays = calculateDurationInDays(dates.start, dates.end);

  // Obtener temporadas activas
  let activeSeasons = [];
  try {
    activeSeasons = await getActiveSeasons(dates.start);
  } catch (error) {
    console.warn('Error obteniendo temporadas activas:', error);
    activeSeasons = [];
  }

  // Calcular vehículos con precios estacionales
  const vehicleItems = await Promise.all(
    vehicles.map(async (vehicle) => {
      try {
        // Si el vehículo ya trae precio total/precio por día del backend (por ejemplo desde /api/availability),
        // no recalcular por otro endpoint para evitar inconsistencias (mixed seasons, límites, etc.).
        const canReuseBackendTotalsForTheseDates =
          vehicle?.pricingStart === dates.start && vehicle?.pricingEnd === dates.end;

        if (
          canReuseBackendTotalsForTheseDates &&
          typeof vehicle.totalPrice === 'number' &&
          Number.isFinite(vehicle.totalPrice)
        ) {
          const ppd = (typeof vehicle.pricePerDay === 'number' && Number.isFinite(vehicle.pricePerDay))
            ? vehicle.pricePerDay
            : Math.round(((vehicle.totalPrice / durationInDays) || 0) * 100) / 100;

          return {
            id: vehicle.id,
            model: vehicle.model,
            size: vehicle.size,
            serialNumber: vehicle.serialNumber,
            basePrice: vehicle.basePrice,
            threeToFive: vehicle.threeToFive,
            additionalDay: vehicle.additionalDay,
            pricePerDay: ppd,
            totalPrice: vehicle.totalPrice,
            durationInDays: durationInDays,
            season: vehicle.season ?? null,
            pricingType: vehicle.pricingType ?? 'default',
            discountApplied: 0,
            additionalDaysPrice: 0,
            seasonBreakdown: vehicle.seasonBreakdown || []
          };
        }

        const pricingResult = await calculateDynamicPriceWithSeasonsAsync(vehicle, durationInDays, dates.start, dates.end);
        
        return {
          id: vehicle.id,
          model: vehicle.model,
          size: vehicle.size,
          serialNumber: vehicle.serialNumber,
          basePrice: pricingResult.basePrice,
          threeToFive: pricingResult.threeToFive,
          additionalDay: pricingResult.additionalDay,
          pricePerDay: pricingResult.pricePerDay,
          totalPrice: pricingResult.totalPrice,
          durationInDays: durationInDays,
          season: pricingResult.season,
          pricingType: pricingResult.pricingType,
          discountApplied: pricingResult.discountApplied,
          additionalDaysPrice: pricingResult.additionalDaysPrice,
          seasonBreakdown: pricingResult.seasonBreakdown || []
        };
      } catch (error) {
        console.error('Error calculando precio para vehículo:', vehicle.id, error);
        
        // Fallback a cálculo local
        const localPrice = calculateDynamicPrice(vehicle, durationInDays);
        const pricePerDay = getPricePerDay(vehicle, durationInDays);
        
        return {
          id: vehicle.id,
          model: vehicle.model,
          size: vehicle.size,
          serialNumber: vehicle.serialNumber,
          basePrice: vehicle.basePrice,
          threeToFive: vehicle.threeToFive,
          additionalDay: vehicle.additionalDay,
          pricePerDay: pricePerDay,
          totalPrice: localPrice,
          durationInDays: durationInDays,
          season: null,
          pricingType: 'default',
          discountApplied: 0,
          additionalDaysPrice: 0,
          seasonBreakdown: []
        };
      }
    })
  );

  const vehiclesTotal = Math.round(vehicleItems.reduce((total, item) => total + item.totalPrice, 0) * 100) / 100;

  // Log de depuración para verificar los precios calculados
  console.log('🔍 calculateCompletePricingWithSeasons - vehicleItems:', vehicleItems);
  console.log('🔍 calculateCompletePricingWithSeasons - vehiclesTotal:', vehiclesTotal);

  // Calcular accesorios
  const accessoriesResult = calculateAccessoriesTotal(selectedAccessories, accessoriesList, durationInDays);

  // Obtener tipo de entrega y coste personalizado (para calculateCompletePricingWithSeasons)
  let deliveryType = deliveryTypeOrOption;
  let customDeliveryCost = null;
  if (typeof deliveryTypeOrOption === 'object' && deliveryTypeOrOption !== null) {
    deliveryType = deliveryTypeOrOption.type || 'pickup';
    customDeliveryCost = deliveryTypeOrOption.cost;
  }

  // Calcular coste de entrega
  const deliveryCost = calculateDeliveryCost(deliveryType, customDeliveryCost);

  const insuranceTotal = accessoriesResult.insuranceTotal ?? 0;
  const accessoriesOnlyTotal = Math.max(0, (accessoriesResult.total ?? 0) - insuranceTotal);

  const lineAmounts = [
    ...(vehiclesTotal > 0 ? [{ concept: 'vehicles', amount: vehiclesTotal }] : []),
    ...(insuranceTotal > 0 ? [{ concept: 'insurance', amount: insuranceTotal }] : []),
    ...(accessoriesOnlyTotal > 0 ? [{ concept: 'accessories', amount: accessoriesOnlyTotal }] : []),
    ...(deliveryCost > 0 ? [{ concept: 'delivery', amount: deliveryCost }] : [])
  ];
  const rawGrandTotal = Math.round((vehiclesTotal + accessoriesResult.total + deliveryCost) * 100) / 100;
  const linesWithVat = splitLineAmountsIntoBaseAndVat(lineAmounts, rawGrandTotal, VAT_PERCENT);
  const grandTotal = linesWithVat.reduce((s, l) => s + l.total, 0);

  const vehiclesLine = linesWithVat.find(l => l.concept === 'vehicles');
  const insuranceLine = linesWithVat.find(l => l.concept === 'insurance');
  const accessoriesLine = linesWithVat.find(l => l.concept === 'accessories');
  const deliveryLine = linesWithVat.find(l => l.concept === 'delivery');

  // Determinar tipo de pricing
  const hasSeasonalPricing = vehicleItems.some(item => item.pricingType === 'seasonal');
  const pricingType = hasSeasonalPricing ? 'seasonal' : 'default';
  console.log('🔍 calculateCompletePricingWithSeasons - grandTotal:', grandTotal);

  return {
    durationInDays,
    vehicles: {
      items: vehicleItems,
      total: vehiclesTotal,
      base: vehiclesLine?.base ?? Math.round((vehiclesTotal / (1 + VAT_PERCENT / 100)) * 100) / 100,
      vat: vehiclesLine?.vat ?? Math.round((vehiclesTotal - (vehiclesLine?.base ?? 0)) * 100) / 100
    },
    accessories: {
      items: accessoriesResult.items,
      total: accessoriesResult.total,
      insuranceTotal,
      insuranceBase: insuranceLine?.base ?? 0,
      insuranceVat: insuranceLine?.vat ?? 0,
      accessoriesOnlyTotal,
      accessoriesOnlyBase: accessoriesLine?.base ?? 0,
      accessoriesOnlyVat: accessoriesLine?.vat ?? 0
    },
    delivery: {
      type: deliveryType,
      cost: deliveryCost,
      base: deliveryLine?.base ?? 0,
      vat: deliveryLine?.vat ?? 0
    },
    lines: linesWithVat,
    seasons: activeSeasons,
    pricingType: pricingType,
    grandTotal
  };
};

/**
 * Valida que un vehículo tenga todos los campos de precio necesarios
 * @param {Object} vehicle - Objeto del vehículo a validar
 * @returns {boolean} True si el vehículo es válido
 */
export const validateVehiclePricing = (vehicle) => {
  return vehicle && 
         typeof vehicle.basePrice === 'number' && 
         typeof vehicle.threeToFive === 'number' && 
         typeof vehicle.additionalDay === 'number' &&
         vehicle.basePrice >= 0 &&
         vehicle.threeToFive >= 0 &&
         vehicle.additionalDay >= 0;
}; 